import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios, { AxiosRequestConfig } from 'axios';
import { verifyRoutePermissions } from '../../globalFunctions';
import {
    Line,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    BarChart,
    Bar,
    LineChart
} from 'recharts';
import { Board } from '../../components/Board';
import { NavButton } from '../../components/NavButton';
import { Loading } from '../../components/Loading';
import styles from './styles.module.scss';
import StatsCard from './statsCard';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import 'moment/locale/pt-br';

const COLORS = ['#10b981', '#ef4444', '#3b82f6', '#f59e0b'];

export default function ModuleAnalytics() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [userAuth, setUserAuth] = useState<any>();
    const [reportData, setReportData] = useState<any>();
    const [rankMode,setRank]= useState<'dias' | 'time'>('dias')
    const [dataInicio, setDataInicio] = useState<any>(moment("2023-01-01"));
    const [dataFim, setDataFim] = useState<any>(moment());

const fetchReportData = async () => {
    if (!userAuth) return;

    const config: AxiosRequestConfig = {
        headers: {
            Authorization: `Bearer ${userAuth.token}`,
            Accept: 'application/json'
        },
        params: {
            data_inicio: dataInicio ? moment(dataInicio).format('YYYY-MM-DD') : null,
            data_fim: dataFim ? moment(dataFim).format('YYYY-MM-DD') : null
        }
    };

    const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/logicmanager/module/report/${id}`,
        config
    );
    setReportData(data);
};


    useEffect(() => {
        const user = verifyRoutePermissions(['auth', 'email.verified'], navigate);
        if (typeof user === 'object') setUserAuth(user);
    }, []);

    useEffect(() => {
        fetchReportData();
    }, [userAuth]); 

    useEffect(() => {
    if (userAuth) {
        fetchReportData();
    }
}, [dataInicio, dataFim]);

    if (!reportData) return <Loading />;

    const {
        moduloNome,
        estatisticasGerais,
        taxaPorDia,
        tentativasPorExercicio,
        usuariosCompletaram,
        retencaoUsuarios,
        maisJogado,
        menosJogado,
        maiorDesempenho,
        menorDesempenho
    } = reportData;
    const LineChartA = LineChart as any;
    const ResponsiveContainerA = ResponsiveContainer as any;
    const XAxisA = XAxis as any;
    const YAxisA = YAxis as any;
    const LineA = Line as any;
    const BarChartA = BarChart as any;
    const BarA = Bar as any;
    return (
        <div className={styles.wrapper}>
            <NavButton
                direction='left'
                style={{ left: '1vw', top: '2vw', zIndex: '20' }}
                onClick={() => navigate('/manager/logicmakerModules')}
            />

            <Board wrapperClass={`${styles.boardEstatisticas} ${styles.big}`}>
                <div className={styles.boardEstatisticas}>
                <h1 className={styles.pageTitle}>{moduloNome}</h1>

                <div className={styles.dateFilter}>
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="pt-br">
        
        <DatePicker
            label="Início"
            value={dataInicio}
            onChange={(value) => setDataInicio(value)}
        />

        <DatePicker
            label="Fim"
            value={dataFim}
            onChange={(value) => setDataFim(value)}
        />

    </LocalizationProvider>
</div>



                {/* === HEADER METRICS === */}
                <div className={styles.metricsRow}>
                    <StatsCard title={'Total Jogadas'} data={estatisticasGerais.totalJogadas} />
                    <StatsCard
                        title={'Taxa de Acertos'}
                        data={estatisticasGerais.taxaAcertos + '%'}
                    />
                    <StatsCard title={'Usuários únicos'} data={estatisticasGerais.usuariosUnicos} />
                    <StatsCard
                        title={'Usuários que completaram'}
                        data={usuariosCompletaram.length}
                    />

                    {maisJogado &&
                    <StatsCard
                    title={'Exercicio mais jogado'}
                    data={`${maisJogado.nome}`}
                    />
                }

                {menosJogado &&
                    <StatsCard
                    title={'Exercicio menos jogado'}
                    data={`${menosJogado.nome}`}
                    />
                }

                {maiorDesempenho &&
                    <StatsCard
                        title={'Exercicio com maior Desempenho'}
                        data={`${maiorDesempenho.nome}`}
                    />
                }

               { menorDesempenho &&
                    <StatsCard
                        title={'Exercicio com menor Desempenho'}
                        data={`${menorDesempenho.nome}`}
                    />}
                </div>

                {/* === LINE CHART === */}
                <div className={styles.chartSection}>
                    <div className={styles.chartHeader}>
                        <h2>Evolução da Taxa de Acertos</h2>
                    </div>

                    <ResponsiveContainerA width='100%' height={280}>
                        <LineChartA
                            data={taxaPorDia}
                        >
                            <LineA
                                type='monotone'
                                dataKey='taxa'
                                stroke='#3b82f6'
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                activeDot={{ r: 6 }}
                            />
                            <CartesianGrid strokeDasharray='3 3' stroke='#333' />
                            <XAxisA dataKey='date' stroke='#888' />
                            <YAxisA stroke='#888' />
                            <Tooltip />
                        </LineChartA>
                    </ResponsiveContainerA>
                </div>

                {/* === BAR CHART === */}
                <div className={styles.chartSection}>
                    <h2>Tentativas por Exercício</h2>
                    <ResponsiveContainerA width='100%' height={280}>
                        <BarChartA data={tentativasPorExercicio}>
                            <CartesianGrid strokeDasharray='3 3' stroke='#333' />
                            <XAxisA dataKey='name' stroke='#888' />
                            <YAxisA stroke='#888' />
                            <Tooltip />
                            <BarA dataKey='tentativas' fill='#10b981' radius={[6, 6, 0, 0]} />
                        </BarChartA>
                    </ResponsiveContainerA>
                </div>

                <div className={styles.chartSection}>
                    <h2>Ranking de Alunos</h2>
                    <button style={rankMode== "time"? {backgroundColor: 'white', color: 'black'}:{}} className={styles.rankButton} onClick={()=>setRank('time')}>Completaram o módulo</button>
                    <button  style={rankMode== "dias"? {backgroundColor: 'white', color: 'black'}:{}}className={styles.rankButton} onClick={()=>setRank('dias')}>Dias práticados</button>
                    <table className={styles.table}>
                    { rankMode == 'dias' ? (<>
                        <thead>
                            <tr>
                                <th>Posição</th>
                                <th>Usuário</th>
                                <th>Dias praticados</th>
                            </tr>
                        </thead>
                        <tbody>
                            {retencaoUsuarios.map((user: any, i: number) => (
                                <tr key={i}>
                                    <td>#{i+1}</td>
                                    <td>{user.nome}</td>
                                    <td>{user.dias_praticados}</td>
                                </tr>
                            ))}
                        </tbody>
                    </>
                        ) :
                        (<>
                         <thead>
                            <tr>
                                <th>Posição</th>
                                <th>Aluno</th>
                                <th>Desempenho</th>
                                <th>Tempo Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {usuariosCompletaram.map((user: any, i: number) => (
                                <tr key={i}>
                                    <td>#{i+1}</td>
                                    <td>{user.nome}</td>
                                    <td>{user.desempenho}%</td>
                                    <td>{user.tempo}</td>
                                </tr>
                            ))}
                        </tbody>
                    </>
                        ) 
                        }
                    </table>
                </div>
                </div>
            </Board>
        </div>
    );
}
